// Imports
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptionsMode } from '@stripe/stripe-js';
import currency from 'currency.js';
import { forwardRef, useMemo, useState } from 'react';
import { GetFeeParameters } from '../../../../../../shareables/types/api/main/website/payment-option/fee/get';
import { GetPaymentOptionResponse } from '../../../../../../shareables/types/api/main/website/payment-option/get';
import useStripeAppearance from '../hooks/use-stripe-appearance';
import useStripeInstance from '../../../../../../shareables/front-end/stripe-component-wrappers/hooks/use-stripe-instance';
import StripePaymentElement, { StripePaymentElementRef } from './stripe-payment-element';


// Define the accepted props
interface Props {
	paymentOption: GetPaymentOptionResponse & { id: string };
	selectedAmount: GetPaymentOptionResponse['amounts'][0];
	onSuccess: () => void;
	onPaymentMethodCountryCodeChange: (paymentMethodCountryCode: GetFeeParameters['methodCountryCode']) => void;
	isActive: boolean;
}


// Function component
const StripeElements = forwardRef<StripePaymentElementRef, Props>(
	({ paymentOption, selectedAmount, onSuccess, onPaymentMethodCountryCodeChange, isActive }, ref) => {
		// Use state
		const [saveCard, setSaveCard] = useState(false);
		
		
		// Use Stripe hooks
		const stripe = useStripeInstance(paymentOption.stripeAccountID);
		const appearance = useStripeAppearance();
		
		
		// Create options
		const options = useMemo<StripeElementsOptionsMode>(
			() => ({
				amount: currency(Math.max(selectedAmount, 0.5)).intValue, // Must meet Stripe minimum or option is invalid
				mode: 'payment',
				currency: paymentOption.currencyCode.toLowerCase(),
				payment_method_types: ['card'],
				setupFutureUsage: saveCard ? 'on_session' : null,
				appearance: appearance,
			}),
			[selectedAmount, paymentOption.currencyCode, saveCard, appearance]
		);
		
		
		// Return JSX
		return (
			<Elements stripe={stripe} options={options}>
				<StripePaymentElement
					ref={ref}
					onSuccess={onSuccess}
					paymentOptionID={paymentOption.id}
					onPaymentMethodCountryCodeChange={onPaymentMethodCountryCodeChange}
					onSaveCardChange={setSaveCard}
					isActive={isActive}
				/>
			</Elements>
		);
	}
);


// @ts-expect-error: displayName does exist
StripeElements.displayName = 'StripeElements';

export default StripeElements;
